import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import Modal from '../components/common/Modal'
import toast from 'react-hot-toast'

export default function EmailPackingSlip() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [packingSlips, setPackingSlips] = useState([])
  const [search, setSearch] = useState('')
  
  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [selectedSlip, setSelectedSlip] = useState(null)
  const [emailAddress, setEmailAddress] = useState('')
  const [sending, setSending] = useState(false)

  useEffect(() => {
    loadPackingSlips()
  }, [])

  async function loadPackingSlips() {
    setLoading(true)
    try {
      // Get submitted packing slips (tsType=2, status=1) for current user
      const response = await api.get('/api/timesheets/packing-slips')
      if (response.success) {
        setPackingSlips(response.packingSlips || [])
      } else {
        toast.error('Failed to load packing slips')
      }
    } catch (error) {
      toast.error('Error loading packing slips')
    }
    setLoading(false)
  }

  function handleSelectSlip(slip) {
    setSelectedSlip(slip)
    setEmailAddress(slip.customerEmail || '')
    setShowEmailModal(true)
  }

  async function handleSendEmail() {
    if (!emailAddress.trim()) {
      toast.error('Email address is required')
      return
    }

    if (!emailAddress.includes('@')) {
      toast.error('Please enter a valid email address')
      return
    }

    setSending(true)
    try {
      const response = await api.post('/api/timesheets/send-packing-slip', {
        uuid: selectedSlip.uuid,
        email: emailAddress
      })
      
      if (response.success) {
        toast.success('Packing slip sent!')
        setShowEmailModal(false)
        setSelectedSlip(null)
      } else {
        toast.error(response.message || 'Failed to send email')
      }
    } catch (error) {
      toast.error('Error sending email')
    }
    setSending(false)
  }

  const filteredSlips = packingSlips.filter(ps =>
    (ps.workOrderNumber || '').toLowerCase().includes(search.toLowerCase()) ||
    (ps.customer || '').toLowerCase().includes(search.toLowerCase()) ||
    (ps.referenceNo || '').toLowerCase().includes(search.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8"></div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Email Packing Slip</h1>
        <p className="text-blue-100 mt-1">{filteredSlips.length} submitted packing slips</p>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <Input
          placeholder="Search by job number, customer, or reference..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {filteredSlips.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-4xl mb-2">📦</p>
            <p>No submitted packing slips</p>
            <p className="text-sm mt-2">Submit a packing slip to email it to customers</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredSlips.map((slip) => (
              <button
                key={slip.uuid}
                onClick={() => handleSelectSlip(slip)}
                className="w-full text-left p-4 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800 text-lg">
                      {slip.workOrderNumber || 'No Job #'} - {slip.customer || 'No Customer'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {slip.referenceNo && <span className="mr-3">Ref: {slip.referenceNo}</span>}
                      {slip.month && <span>📅 {slip.month}</span>}
                    </p>
                    {slip.customerEmail && (
                      <p className="text-sm text-gray-400 mt-1">✉️ {slip.customerEmail}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-rdi-blue font-medium">Send Email</span>
                    <svg className="w-5 h-5 text-rdi-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Email Modal */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title="Send Packing Slip"
        size="md"
      >
        {selectedSlip && (
          <div>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="font-medium">{selectedSlip.workOrderNumber} - {selectedSlip.customer}</p>
              <p className="text-sm text-gray-500">Ref: {selectedSlip.referenceNo}</p>
            </div>

            <Input
              label="Email Address"
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="customer@example.com"
            />

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail} loading={sending}>
                Send Email
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}